import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { Badge, Box, Container, Tab, Tabs } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import parseSessionStorageVariable from '../../helpers/utils/parseSessionStorage';
import useInbox from '../../hooks/inbox/useInbox';
import InboxTaskTable from './InboxTaskTable.jsx';
import InboxTicketTable from './InboxTicketTable.jsx';
import { setSnackBar } from '../../states/app/staticSlice.js';

const Inbox = () => {
  const profile = useSelector((state) => state?.login?.profile);
  const [contentTab, setContentTab] = useState(
    parseSessionStorageVariable('content_tab_inbox') || 'Tickets'
  );
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState(
    parseSessionStorageVariable('status_inbox') || 0
  );
  const [isClose, setIsClose] = useState(
    parseSessionStorageVariable('is_close_inbox') || false
  );
  const [toModerate, setToModerate] = useState(
    parseSessionStorageVariable('to_moderate_inbox') || false
  );
  const [sellerType, setSellerType] = useState(
    parseSessionStorageVariable('seller_type_inbox') || null
  );
  const [isDone, setIsDone] = useState(
    parseSessionStorageVariable('is_done_inbox') || false
  );
  const [assignedTypeTicket, setAssignedTypeTicket] = useState(
    parseSessionStorageVariable('assigned_type_ticket_inbox') || true
  );
  const [assignedTypeTask, setAssignedTypeTask] = useState(
    parseSessionStorageVariable('assigned_type_task_inbox') || null
  );
  const [hasNewResponse, setHasNewResponse] = useState(
    parseSessionStorageVariable('has_new_response_inbox') || false
  );
  const [orderBy, setOrderBy] = useState(
    parseSessionStorageVariable('order_by_inbox') || null
  );
  const [order, setOrder] = useState(
    parseSessionStorageVariable('order_inbox')
  );
  const [inboxData, setInboxData] = useState({
    // Common
    limit: limit,
    offset: offset,
    content: contentTab, // Tickets / Tasks
    // Tickets
    isClose: isClose, // true / false
    toModerate: toModerate, // true / false
    sellerType: sellerType, // true / false / null
    hasNewResponse: hasNewResponse, // true / false
    status: status, // 0 : New / In Progress - 1 : Waiting - 2 : Done
    orderBy: orderBy, // null / "status"
    order: order, // "asc" / "desc"
    assignedTypeTicket: assignedTypeTicket,
    // Tasks
    isDone: isDone, // true / false
    assignedTypeTask: assignedTypeTask,
  });
  const { data, res, isLoading, isValidating, mutate } = useInbox(
    profile?._id,
    inboxData
  );
  const dispatch = useDispatch();

  useEffect(() => {
    sessionStorage.setItem('is_close_inbox', isClose);
  }, [isClose]);

  useEffect(() => {
    sessionStorage.setItem('to_moderate_inbox', toModerate);
  }, [toModerate]);

  useEffect(() => {
    sessionStorage.setItem('seller_type_inbox', sellerType);
  }, [sellerType]);

  useEffect(() => {
    sessionStorage.setItem('is_done_inbox', isDone);
  }, [isDone]);

  useEffect(() => {
    sessionStorage.setItem('assigned_type_ticket_inbox', assignedTypeTicket);
  }, [assignedTypeTicket]);

  useEffect(() => {
    sessionStorage.setItem('assigned_type_task_inbox', assignedTypeTask);
  }, [assignedTypeTask]);

  useEffect(() => {
    sessionStorage.setItem('has_new_response_inbox', hasNewResponse);
  }, [hasNewResponse]);

  useEffect(() => {
    sessionStorage.setItem('content_tab_inbox', contentTab);
  }, [contentTab]);

  useEffect(() => {
    sessionStorage.setItem('status_inbox', status);
  }, [status]);

  useEffect(() => {
    sessionStorage.setItem('order_by_inbox', orderBy);
  }, [orderBy]);

  useEffect(() => {
    sessionStorage.setItem('order_inbox', order);
  }, [order]);

  useEffect(() => {
    setInboxData({
      // Common
      limit: limit,
      offset: offset,
      content: contentTab, // Tickets / Tasks
      // Tickets
      isClose: isClose, // true / false
      toModerate: toModerate, // true / false
      sellerType: sellerType, // true / false / null
      hasNewResponse: hasNewResponse, // true / false
      status: status, // 0 : New / In Progress - 1 : Waiting - 2 : Done
      orderBy: orderBy, // null / "status"
      order: order, // "asc" / "desc"
      assignedTypeTicket: assignedTypeTicket,
      // Tasks
      isDone: isDone, // true / false
      assignedTypeTask: assignedTypeTask,
    });
  }, [
    limit,
    offset,
    contentTab,
    isClose,
    toModerate,
    sellerType,
    hasNewResponse,
    status,
    isDone,
    assignedTypeTicket,
    assignedTypeTask,
    orderBy,
    order,
  ]);

  useEffect(() => {
    setOffset(page * limit);
  }, [page, limit]);

  useEffect(() => {
    setPage(0);
  }, [
    contentTab,
    isClose,
    toModerate,
    sellerType,
    hasNewResponse,
    status,
    isDone,
    assignedTypeTicket,
    assignedTypeTask,
  ]);

  useEffect(() => {
    mutate();
    if (res && res?.status > 300) {
      const messageError = `Error: ${res?.status} - ${res?.statusText}: Inbox`;
      dispatch(
        setSnackBar({ open: true, message: messageError, color: 'error' })
      );
    } else {
      dispatch(setSnackBar({ open: false, message: null, color: null }));
    }
  }, [inboxData]);

  const handleContentChange = (event, newValue) => {
    setContentTab(newValue);
    setPage(0);
  };

  return (
    <Container maxWidth="full">
      <Box
        sx={{
          display: 'flex',
          marginLeft: -4,
          marginTop: 2,
        }}
      >
        <Tabs
          orientation="vertical"
          // variant="scrollable"
          value={contentTab}
          onChange={handleContentChange}
          sx={{ width: 80 }}
        >
          <Tab
            icon={<ConfirmationNumberIcon />}
            id={0}
            value={'Tickets'}
            label={
              <>
                <Badge
                  color="error"
                  badgeContent={data ? data?.tickets?.length : 0}
                  sx={{
                    '& .MuiBadge-badge': {
                      height: 'fit-content',
                      width: 'fit-content',
                    },
                  }}
                ></Badge>
              </>
            }
          />

          <Tab
            icon={<TaskAltIcon />}
            id={1}
            value={'Tasks'}
            label={
              <>
                <Badge
                  color="error"
                  badgeContent={data ? data?.tasks?.length : 0}
                  sx={{
                    '& .MuiBadge-badge': {
                      height: 'fit-content',
                      width: 'fit-content',
                    },
                  }}
                ></Badge>
              </>
            }
          />
        </Tabs>
        <Box sx={{ padding: '0.1rem', width: '112rem' }}>
          {contentTab === 'Tickets' ? (
            <InboxTicketTable
              loading={isLoading}
              validating={isValidating}
              datas={data?.data}
              inboxData={inboxData}
              limit={limit}
              setLimit={setLimit}
              page={page}
              setPage={setPage}
              setAssignedTypeTicket={setAssignedTypeTicket}
              setStatus={setStatus}
              setIsClose={setIsClose}
              setToModerate={setToModerate}
              setSellerType={setSellerType}
              setHasNewResponse={setHasNewResponse}
              setOrderBy={setOrderBy}
              setOrder={setOrder}
              total={data?.length}
              mutate={mutate}
            />
          ) : contentTab === 'Tasks' ? (
            <InboxTaskTable
              loading={isLoading}
              validating={isValidating}
              datas={data?.data}
              inboxData={inboxData}
              limit={limit}
              setLimit={setLimit}
              page={page}
              setPage={setPage}
              setIsDone={setIsDone}
              setAssignedTypeTask={setAssignedTypeTask}
              total={data?.length}
              mutate={mutate}
            />
          ) : null}
        </Box>
      </Box>
    </Container>
  );
};

export default Inbox;
